import { type Component, createEffect, createMemo, createSignal } from 'solid-js'
import { createStore } from 'solid-js/store'
import Button from '~/components/Button'
import Input from '~/components/Input'
import MaskInput from '~/components/MaskInput'
import Modal from '~/components/Modal'
import Select from '~/components/Select'
import Textarea from '~/components/Textarea'
import type { ServiceItemType } from '~/services/serviceCatalog.service'
import DiscountField from './DiscountField'
import { emptyLine, type LineType, lineDiscount, lineFromService, lineSubtotal } from './line'

export interface LineItemModalPropsType {
  isOpen: boolean
  onClose: () => void
  /** Devuelve la línea lista para guardar (añadir o reemplazar) en la lista del formulario. */
  onAdd: (line: LineType) => void
  /** Línea en edición; null/undefined = nueva línea libre. */
  initialLine?: LineType | null
  /** Opciones del catálogo para el Select. */
  catalogOptions: { value: string; title: string; subtitle?: string }[]
  /** Los servicios completos del catálogo, para pre-rellenar la línea. */
  services: ServiceItemType[]
  currency: string
  locale: string
  formatMoney: (value: number) => string
  /** Rótulo del primer campo (Concepto o Servicio). */
  lineLabel?: string
  placeholder?: string
}

/**
 * Alta (o edición) de un concepto para una cotización o cuenta de cobro. Se puede
 * partir de un servicio del catálogo (que pre-rellena la línea) o escribir uno
 * libre; los importes se calculan igual que el backend.
 */
const LineItemModal: Component<LineItemModalPropsType> = props => {
  const [draft, setDraft] = createStore<LineType>(emptyLine())
  const [pickedId, setPickedId] = createSignal('')

  createEffect(() => {
    if (!props.isOpen) return
    setDraft(props.initialLine ?? emptyLine())
    setPickedId('')
  })

  const pickFromCatalog = (id: string) => {
    setPickedId(id)
    const service = props.services.find(s => String(s.id) === id)
    if (service) setDraft(lineFromService(service))
  }

  const isEditing = () => props.initialLine != null
  const valid = createMemo(() => draft.description.trim().length > 0)

  const handleAdd = () => {
    if (!valid()) return
    props.onAdd({ ...draft, description: draft.description.trim(), notes: draft.notes.trim() })
    props.onClose()
  }

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="md">
      <Modal.Header>
        <h2 class="text-lg font-bold text-foreground">
          {isEditing() ? 'Editar' : 'Añadir'} {props.lineLabel ?? 'concepto'}
        </h2>
        <p class="text-sm text-foreground-subtle">{isEditing() ? 'Ajusta la línea y guarda los cambios' : 'Elige uno del catálogo o escribe una línea libre'}</p>
      </Modal.Header>
      <Modal.Body>
        <div class="space-y-4">
          <Select
            label="Del catálogo"
            options={props.catalogOptions}
            value={pickedId()}
            onChange={v => pickFromCatalog(String(v ?? ''))}
            placeholder="Selecciona un servicio del catálogo"
            autocomplete
            clearable
          />

          <Textarea label={props.lineLabel ?? 'Concepto'} rows={1} value={draft.description} placeholder="Describe el concepto" onInput={e => setDraft('description', e.currentTarget.value)} />

          <div class="grid grid-cols-2 gap-3 items-end">
            <Input label="Cantidad" type="number" min="0" step="0.01" value={draft.quantity} onInput={e => setDraft('quantity', Number(e.currentTarget.value) || 0)} />
            <MaskInput
              label="Precio unitario"
              maskType="currency"
              currency={props.currency}
              locale={props.locale}
              value={draft.unitPrice ? String(draft.unitPrice) : ''}
              onValueChange={v => setDraft('unitPrice', Number(v) || 0)}
            />
          </div>

          <div class="grid grid-cols-2 gap-3 items-end">
            <DiscountField
              value={draft.discount}
              type={draft.discountType}
              currency={props.currency}
              locale={props.locale}
              resolved={lineDiscount(draft)}
              formatMoney={props.formatMoney}
              onValueChange={v => setDraft('discount', v)}
              onTypeChange={v => setDraft('discountType', v)}
            />
            <div class="rounded-lg bg-surface border border-border px-3 py-2 text-sm">
              <p class="text-[11px] uppercase tracking-wide text-foreground-subtle">Total</p>
              <p class="font-semibold text-foreground truncate">{props.formatMoney(lineSubtotal(draft))}</p>
            </div>
          </div>

          <Textarea label="Notas" rows={1} value={draft.notes} placeholder="Detalle adicional (opcional)" onInput={e => setDraft('notes', e.currentTarget.value)} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div class="flex justify-end gap-2">
          <Button type="button" variant="soft" color="default" onClick={props.onClose}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleAdd} disabled={!valid()} prependIcon={isEditing() ? 'material-symbols-light:save-outline' : 'material-symbols-light:add'}>
            {isEditing() ? 'Guardar' : 'Añadir'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default LineItemModal
